import { useState } from "react";
import type { FormEvent } from "react";
import { categories } from "@/mocks/kuaibao";

const FORM_ID = "daio003oh653ivfvoh6g";
const SUBMIT_ADDR = "https://readdy.ai/api/form/daio003oh653ivfvoh6g";

type Status = "idle" | "submitting" | "success" | "error";

export default function SubscribeSection() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const fd = new FormData(form);

    const honeypot = String(fd.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("订阅成功，我们会把每日瓜单送到你的邮箱。");
      form.reset();
      return;
    }
    fd.delete("website_alt");

    const email = String(fd.get("email") || "").trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setMessage("请填写一个有效的邮箱地址，我们才能把快报发给你。");
      return;
    }

    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (typeof value === "string") payload.append(key, value);
    });

    setStatus("submitting");
    setMessage("正在提交…");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setMessage("订阅成功，我们会把每日瓜单送到你的邮箱。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(
          serverMsg && serverMsg.trim()
            ? serverMsg
            : "提交没有成功，请稍后再试或更换邮箱地址。",
        );
      }
    } catch {
      setStatus("error");
      setMessage("网络似乎有点问题，请稍后重试。");
    }
  };

  return (
    <section id="subscribe" className="w-full bg-background-50 py-14 md:py-20">
      <div className="w-full px-4 md:px-8">
        <div className="rounded-lg bg-primary-950 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="p-6 md:p-10 lg:p-12">
              <div className="inline-flex items-center gap-2 mb-4">
                <span className="w-2 h-2 rounded-full bg-accent-400"></span>
                <span className="font-label text-xs tracking-[0.2em] text-accent-400">
                  DAILY DIGEST
                </span>
              </div>
              <h2 className="font-heading font-bold text-2xl md:text-4xl text-background-50 leading-snug mb-4">
                订阅今日吃瓜单
              </h2>
              <p className="font-label text-sm text-background-300 leading-relaxed mb-6 max-w-md">
                每天一份邮件摘要，只挑当天最值得看的十条热点。
                不用注册账号，随时可以退订，不会塞满你的收件箱。
              </p>
              <ul className="space-y-3 font-label text-sm text-background-200">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-accent-400 leading-none"></i>
                  每日一次汇总，拒绝频繁推送
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-accent-400 leading-none"></i>
                  可按兴趣只看指定分类
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-accent-400 leading-none"></i>
                  邮箱仅用于发送快报，不作他用
                </li>
              </ul>
            </div>

            <div className="p-6 md:p-10 lg:p-12 bg-background-50">
              <form
                id={FORM_ID}
                data-readdy-form
                onSubmit={handleSubmit}
                className="flex flex-col gap-4"
              >
                <div>
                  <label
                    htmlFor="sub-email"
                    className="block font-label text-sm text-foreground-700 mb-2"
                  >
                    邮箱地址 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="sub-email"
                    type="email"
                    name="email"
                    required
                    placeholder="you@example.com"
                    className="w-full h-11 px-3.5 rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 font-label text-sm outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                  />
                </div>

                <div>
                  <label
                    htmlFor="sub-nickname"
                    className="block font-label text-sm text-foreground-700 mb-2"
                  >
                    称呼（选填）
                  </label>
                  <input
                    id="sub-nickname"
                    type="text"
                    name="nickname"
                    placeholder="怎么称呼你"
                    className="w-full h-11 px-3.5 rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 font-label text-sm outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                  />
                </div>

                <div>
                  <label
                    htmlFor="sub-interest"
                    className="block font-label text-sm text-foreground-700 mb-2"
                  >
                    最想看的方向
                  </label>
                  <select
                    id="sub-interest"
                    name="interest"
                    defaultValue="all"
                    className="w-full h-11 px-3.5 rounded-md border border-background-300 bg-background-50 text-foreground-900 font-label text-sm outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 cursor-pointer"
                  >
                    <option value="all">全部热点</option>
                    {categories.map((cat) => (
                      <option key={cat.slug} value={cat.name}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-alt-field" aria-hidden="true">
                  <label htmlFor="sub-website-alt">请勿填写此栏</label>
                  <input
                    id="sub-website-alt"
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                  />
                </div>

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="mt-1 h-11 rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 font-label text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                >
                  {status === "submitting" ? "提交中…" : "免费订阅每日快报"}
                </button>

                {status !== "idle" && (
                  <p
                    role="status"
                    className={`font-label text-sm rounded-md px-3.5 py-2.5 ${
                      status === "success"
                        ? "bg-secondary-100 text-secondary-900"
                        : status === "error"
                          ? "bg-primary-100 text-primary-900"
                          : "bg-background-100 text-foreground-600"
                    }`}
                  >
                    {message}
                  </p>
                )}

                <p className="font-label text-xs text-foreground-400">
                  点击订阅即表示你同意我们仅将邮箱用于发送热点快报。
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}